import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { ValidateResult } from '../../../Vendor/react-hook-form'
import { RulesLanguage } from '../../../../i18n'
import { IsStringEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { EMAIL_REGEX } from '../../../NonReact/constants/Regexes'

type Rule<T extends [...unknown[]]> = (
    language: CLanguageHandler,
    languageKey?: string,
    ...args: T
) => (value: string) => ValidateResult

export type RulesType = {
    isNumber: Rule<[]>
    isInteger: Rule<[]>
    isPositiveNumber: Rule<[]>
    isUnderMaxSafeInteger: Rule<[]>
    isUnderLimit: Rule<[number, boolean]>
    isEmailValid: Rule<[]>
    mustMatch: Rule<[string]>
    minLength: Rule<[Definable<number>]>
    isValidUrl: Rule<[]>
    isEmpty: Rule<[]>
}

const loadLanguage = (language: CLanguageHandler, languageKey?: string): void => {
    if (IsStringEmpty(languageKey)) {
        language.loadBundle(RulesLanguage.NAME)
    }
}

export const Rules: RulesType = {
    /**
     * Rule to validate a string is a number.
     */
    isNumber:
        (language: CLanguageHandler) =>
        (value: string): ValidateResult => {
            loadLanguage(language)

            if (isStringNotEmpty(value) && isNaN(Number(value))) {
                return language.get(RulesLanguage.Rules_Validation_MustBeANumber)
            }

            return undefined
        },

    /**
     * Rule to validate a string is an integer.
     */
    isInteger:
        (language: CLanguageHandler) =>
        (value: string): ValidateResult => {
            loadLanguage(language)

            const isNumberRes: ValidateResult = Rules.isNumber(language)(value)

            if (isVarNotEmpty(isNumberRes)) {
                return isNumberRes
            }

            if (isStringNotEmpty(value) && !Number.isInteger(Number(value))) {
                return language.get(RulesLanguage.Rules_Validation_MustBeAnInteger)
            }

            return undefined
        },

    /**
     * Rule to validate a string is a positive number.
     */
    isPositiveNumber:
        (language: CLanguageHandler) =>
        (value: string): ValidateResult => {
            loadLanguage(language)

            const isNumberRes: ValidateResult = Rules.isNumber(language)(value)

            if (isVarNotEmpty(isNumberRes)) {
                return isNumberRes
            }

            if (isStringNotEmpty(value) && Number(value) < 0) {
                return language.get(RulesLanguage.Rules_Validation_MustBeGreaterThanZero)
            }

            return undefined
        },

    /**
     * Rules to validate a string is a number less than or equal to MAX_SAFE_INTEGER.
     */
    isUnderMaxSafeInteger:
        (language: CLanguageHandler) =>
        (value: string): ValidateResult => {
            loadLanguage(language)

            const isNumberRes: ValidateResult = Rules.isNumber(language)(value)

            if (isVarNotEmpty(isNumberRes)) {
                return isNumberRes
            }

            if (isStringNotEmpty(value) && Number(value) > Number.MAX_SAFE_INTEGER) {
                return language.get(RulesLanguage.Rules_Validation_MustBeLessThanMaxSafeInteger)
            }

            return undefined
        },

    /**
     * Rule to validate a string is a number less than or equal to a given limit.
     */
    isUnderLimit:
        (language: CLanguageHandler, languageKey?: string, ...[limit, isRequired]: [number, boolean]) =>
        (value: string): ValidateResult => {
            loadLanguage(language, languageKey)

            const isNumberRes: ValidateResult = Rules.isNumber(language)(value)

            if (isVarNotEmpty(isNumberRes)) {
                return isNumberRes
            }

            if (
                (isVarNotEmpty(limit) && isStringNotEmpty(value) && Number(value) > limit) ||
                (isRequired && IsStringEmpty(value))
            ) {
                return language.getInterpolated(
                    languageKey ?? RulesLanguage.Rules_Validation_MustBeLessThanLimit,
                    limit === Number.MAX_SAFE_INTEGER ? language.get(RulesLanguage.Rules_Numbers_MaxSafeInteger) : limit
                )
            }

            return undefined
        },

    /**
     * Rule to validate an email is valid.
     */
    isEmailValid:
        (language: CLanguageHandler, languageKey?: string) =>
        (email: string): ValidateResult => {
            const lang: CLanguageHandler = language ?? CLanguageHandler.getInstance()

            loadLanguage(lang, languageKey)

            if (!IsStringEmpty(email) && !email?.match(EMAIL_REGEX)) {
                return lang.get(languageKey ?? RulesLanguage.Rules_Validation_EmailMustBeValid)
            }

            return undefined
        },

    /**
     * Rule to validate a string matches a given value.
     *
     * @param language - The language handler.
     * @param languageKey - The language key.
     * @param matchValue - The value to match.
     */
    mustMatch:
        (language: CLanguageHandler, languageKey?: string, ...[matchValue]: [string]) =>
        (value: string): ValidateResult => {
            loadLanguage(language, languageKey)

            if (value !== matchValue) {
                return language.get(languageKey ?? RulesLanguage.Rules_Validation_MustMatch)
            }

            return undefined
        },

    /**
     * Rule to validate a string is greater than a given length.
     *
     * @param language - The language handler.
     * @param languageKey - The language key.
     * @param minLength - The minimum length.
     */
    minLength:
        (language: CLanguageHandler, languageKey?: string, ...[minLength]: [Definable<number>]) =>
        (value: string): ValidateResult => {
            loadLanguage(language, languageKey)

            if (isStringNotEmpty(value) && value.length < (minLength ?? 0)) {
                return language.getInterpolated(languageKey ?? RulesLanguage.Rules_Validation_MinLength, minLength)
            }

            return undefined
        },

    isValidUrl:
        (language: CLanguageHandler, languageKey?: string) =>
        (value: string): ValidateResult => {
            loadLanguage(language, languageKey)

            if (IsStringEmpty(value)) return undefined

            try {
                // eslint-disable-next-line no-new
                new URL(value)
                return undefined
            } catch {
                return language.get(languageKey ?? RulesLanguage.Rules_Validation_ValidUrl)
            }
        },

    isEmpty:
        (language: CLanguageHandler, languageKey?: string) =>
        (value: string): ValidateResult => {
            loadLanguage(language, languageKey)

            if (IsStringEmpty(value)) {
                return language.get(languageKey ?? RulesLanguage.Rules_Validate_Empty)
            }

            return undefined
        }
}
